// src/components/landing/ApostilasBlock.tsx
// Bloco 05 — Apostilas Inteligentes | GRAIA Landing
// Prefixo CSS: aptb-

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Highlighter, MessageSquare, HelpCircle,
    List, Zap
} from 'lucide-react';
import './ApostilasBlock.css';

// ─────────────────────────────────────────────
// DADOS
// ─────────────────────────────────────────────

const CHAPTERS = [
    { id: 1, title: 'Potencial de Ação', progress: 100, hasQuiz: true },
    { id: 2, title: 'Sinapses Químicas', progress: 73, hasQuiz: true },
    { id: 3, title: 'Neurotransmissores', progress: 40, hasQuiz: false },
    { id: 4, title: 'Plasticidade Sináptica', progress: 0, hasQuiz: false },
];

const PARAGRAPH_SEGMENTS = [
    { id: 'p1', text: 'O potencial de ação é uma variação rápida e transitória do potencial de membrana.', highlight: false, note: null as string | null, quiz: false },
    { id: 'p2', text: ' Ocorre quando a despolarização atinge o limiar de disparo, em torno de −55 mV,', highlight: false, note: null as string | null, quiz: false },
    { id: 'p3', text: ' ativando os canais de sódio voltagem-dependentes.', highlight: false, note: null as string | null, quiz: true },
    { id: 'p4', text: ' A fase de repolarização é mediada pela abertura dos canais de potássio e pelo fechamento dos canais de sódio.', highlight: false, note: null as string | null, quiz: false },
];

const FLASHCARD_IN_TEXT = {
    front: 'Qual o limiar de disparo típico do potencial de ação?',
    back: 'Aproximadamente −55 mV, quando a despolarização é suficiente para abrir os canais de Na⁺ voltagem-dependentes.',
};

const QUIZ_QUESTION = {
    text: 'Qual canal é responsável pela REPOLARIZAÇÃO do potencial de ação?',
    options: [
        { key: 'A', text: 'Canal de sódio voltagem-dependente', correct: false },
        { key: 'B', text: 'Canal de potássio voltagem-dependente', correct: true },
        { key: 'C', text: 'Canal de cálcio', correct: false },
    ],
};

const PILLARS = [
    {
        icon: Zap,
        title: 'Flashcards no contexto da leitura',
        text: 'Flashcards aparecem integrados ao texto, vinculados ao parágrafo exato. Você revisa sem sair da apostila.',
    },
    {
        icon: Highlighter,
        title: 'Grifos e notas persistentes',
        text: 'Selecione qualquer trecho e marque. Os grifos ficam salvos, podem ter comentários e são visíveis em todas as visitas.',
    },
    {
        icon: HelpCircle,
        title: 'Quiz dentro do conteúdo',
        text: 'Micro-perguntas integradas ao parágrafo. Você testa o entendimento no momento da leitura.',
    },
];

// ─────────────────────────────────────────────
// COMPONENTE DEMO — inalterado
// ─────────────────────────────────────────────

type ToolbarAction = 'highlight' | 'note' | 'quiz' | null;
type DemoPhase = 'reading' | 'highlight' | 'note' | 'quiz' | 'flashcard' | 'nav' | 'done';

const ApostilasDemo: React.FC<{ active: boolean }> = ({ active }) => {
    const [, setPhase] = useState<DemoPhase>('reading');
    const [segments, setSegments] = useState(PARAGRAPH_SEGMENTS);
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarTarget, setToolbarTarget] = useState<string | null>(null);
    const [activeAction, setActiveAction] = useState<ToolbarAction>(null);
    const [noteText, setNoteText] = useState('');
    const [noteVisible, setNoteVisible] = useState(false);
    const [, setNoteTarget] = useState<string | null>(null);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [quizSelected, setQuizSelected] = useState<string | null>(null);
    const [quizAnswered, setQuizAnswered] = useState(false);
    const [showFlashcard, setShowFlashcard] = useState(false);
    const [flashcardFlipped, setFlashcardFlipped] = useState(false);
    const [showNav, setShowNav] = useState(false);

    const resetDemo = useCallback(() => {
        setPhase('reading');
        setSegments(PARAGRAPH_SEGMENTS);
        setShowToolbar(false);
        setToolbarTarget(null);
        setActiveAction(null);
        setNoteText('');
        setNoteVisible(false);
        setNoteTarget(null);
        setShowQuizModal(false);
        setQuizSelected(null);
        setQuizAnswered(false);
        setShowFlashcard(false);
        setFlashcardFlipped(false);
        setShowNav(false);
    }, []);

    useEffect(() => {
        if (!active) { resetDemo(); return; }

        const timers: ReturnType<typeof setTimeout>[] = [];

        timers.push(setTimeout(() => { setPhase('highlight'); setToolbarTarget('p2'); setShowToolbar(true); }, 500));
        timers.push(setTimeout(() => { setActiveAction('highlight'); }, 1100));
        timers.push(setTimeout(() => {
            setSegments(prev => prev.map(s => s.id === 'p2' ? { ...s, highlight: true } : s));
            setShowToolbar(false); setActiveAction(null); setToolbarTarget(null);
        }, 1500));

        timers.push(setTimeout(() => { setPhase('note'); setToolbarTarget('p1'); setShowToolbar(true); }, 2800));
        timers.push(setTimeout(() => {
            setActiveAction('note'); setShowToolbar(false); setNoteTarget('p1'); setNoteVisible(true);
        }, 3400));

        const noteContent = 'Revisar mecanismo molecular.';
        noteContent.split('').forEach((char, i) => {
            timers.push(setTimeout(() => { setNoteText(prev => prev + char); }, 3700 + i * 45));
        });
        timers.push(setTimeout(() => {
            setSegments(prev => prev.map(s => s.id === 'p1' ? { ...s, note: 'Revisar mecanismo molecular.' } : s));
            setNoteVisible(false); setActiveAction(null); setNoteTarget(null);
        }, 3700 + noteContent.length * 45 + 400));

        timers.push(setTimeout(() => { setPhase('flashcard'); setShowFlashcard(true); }, 6400));
        timers.push(setTimeout(() => { setFlashcardFlipped(true); }, 7800));
        timers.push(setTimeout(() => { setShowFlashcard(false); setFlashcardFlipped(false); }, 9200));

        timers.push(setTimeout(() => { setPhase('quiz'); setShowQuizModal(true); }, 10200));
        timers.push(setTimeout(() => { setQuizSelected('B'); }, 11000));
        timers.push(setTimeout(() => { setQuizAnswered(true); }, 11500));
        timers.push(setTimeout(() => { setShowQuizModal(false); setQuizSelected(null); setQuizAnswered(false); }, 12800));

        timers.push(setTimeout(() => { setPhase('nav'); setShowNav(true); }, 13600));
        timers.push(setTimeout(() => { setShowNav(false); setPhase('done'); }, 15400));

        return () => timers.forEach(clearTimeout);
    }, [active, resetDemo]);

    return (
        <div className="aptb-demo-panel">
            <div className="aptb-mock-header">
                <span className="aptb-mock-logo">GRAIA</span>
                <span className="aptb-mock-sep" />
                <span className="aptb-mock-subtitle">Apostilas</span>
            </div>

            <div className="aptb-reader-body">
                <div className="aptb-breadcrumb">
                    <span>Neurociência</span>
                    <span className="aptb-breadcrumb-sep">›</span>
                    <span className="aptb-breadcrumb-active">Capítulo 2 · Sinapses Químicas</span>
                </div>

                <div className="aptb-reader-text">
                    <h3 className="aptb-chapter-title">Potencial de Ação</h3>

                    <p className="aptb-paragraph">
                        {segments.map(seg => (
                            <span
                                key={seg.id}
                                className={[
                                    'aptb-seg',
                                    seg.highlight ? 'aptb-seg--highlight' : '',
                                    seg.note ? 'aptb-seg--noted' : '',
                                    seg.quiz ? 'aptb-seg--quiz' : '',
                                    toolbarTarget === seg.id ? 'aptb-seg--selecting' : '',
                                ].join(' ')}
                            >
                                {seg.text}
                                {seg.note && (
                                    <span className="aptb-note-marker" title={seg.note}>
                                        <MessageSquare size={9} />
                                    </span>
                                )}
                                {seg.quiz && (
                                    <button className="aptb-quiz-badge" onClick={() => setShowQuizModal(true)}>
                                        Quiz
                                    </button>
                                )}
                            </span>
                        ))}
                    </p>

                    {showFlashcard && (
                        <div
                            className={`aptb-inline-flashcard aptb-inline-flashcard--emerge ${showFlashcard ? 'aptb-inline-flashcard--visible' : ''}`}
                            onClick={() => setFlashcardFlipped(f => !f)}
                        >
                            <div className="aptb-inline-fc-header">
                                <Zap size={11} />
                                Flashcard vinculado
                            </div>
                            <div className="aptb-inline-fc-content">
                                {!flashcardFlipped ? (
                                    <p className="aptb-inline-fc-front">{FLASHCARD_IN_TEXT.front}</p>
                                ) : (
                                    <p className="aptb-inline-fc-back">{FLASHCARD_IN_TEXT.back}</p>
                                )}
                            </div>
                            <span className="aptb-inline-fc-hint">
                                {flashcardFlipped ? '← Frente' : 'Toque para resposta'}
                            </span>
                        </div>
                    )}
                </div>

                {showToolbar && (
                    <div className="aptb-toolbar">
                        <button className={`aptb-toolbar-btn ${activeAction === 'highlight' ? 'aptb-toolbar-btn--active' : ''}`}>
                            <Highlighter size={12} /> Grifar
                        </button>
                        <button className={`aptb-toolbar-btn ${activeAction === 'note' ? 'aptb-toolbar-btn--active' : ''}`}>
                            <MessageSquare size={12} /> Nota
                        </button>
                        <button className={`aptb-toolbar-btn ${activeAction === 'quiz' ? 'aptb-toolbar-btn--active' : ''}`}>
                            <HelpCircle size={12} /> Quiz
                        </button>
                    </div>
                )}

                {noteVisible && (
                    <div className="aptb-note-balloon">
                        <p className="aptb-note-balloon-label">Adicionar nota</p>
                        <div className="aptb-note-input">
                            {noteText}
                            <span className="aptb-note-cursor" />
                        </div>
                    </div>
                )}
            </div>

            {showQuizModal && (
                <div className="aptb-quiz-overlay aptb-quiz-overlay--blur">
                    <div className="aptb-quiz-modal">
                        <div className="aptb-quiz-modal-header">
                            <HelpCircle size={13} /> Quiz contextual
                        </div>
                        <p className="aptb-quiz-question">{QUIZ_QUESTION.text}</p>
                        <div className="aptb-quiz-options">
                            {QUIZ_QUESTION.options.map(opt => (
                                <button
                                    key={opt.key}
                                    className={[
                                        'aptb-quiz-option',
                                        quizSelected === opt.key && !quizAnswered ? 'aptb-quiz-option--selected' : '',
                                        quizAnswered && opt.correct ? 'aptb-quiz-option--correct' : '',
                                        quizAnswered && quizSelected === opt.key && !opt.correct ? 'aptb-quiz-option--wrong' : '',
                                    ].join(' ')}
                                    onClick={() => !quizAnswered && setQuizSelected(opt.key)}
                                    disabled={quizAnswered}
                                >
                                    <span className="aptb-quiz-key">{opt.key}</span>
                                    <span className="aptb-quiz-opt-text">{opt.text}</span>
                                </button>
                            ))}
                        </div>
                        {!quizAnswered ? (
                            <button className="aptb-quiz-submit" disabled={!quizSelected} onClick={() => setQuizAnswered(true)}>
                                Responder
                            </button>
                        ) : (
                            <button className="aptb-quiz-close" onClick={() => { setShowQuizModal(false); setQuizSelected(null); setQuizAnswered(false); }}>
                                Continuar lendo
                            </button>
                        )}
                    </div>
                </div>
            )}

            {showNav && (
                <div className="aptb-nav-panel">
                    <div className="aptb-nav-header"><List size={13} /> Índice</div>
                    {CHAPTERS.map(ch => (
                        <div key={ch.id} className={`aptb-nav-item ${ch.id === 2 ? 'aptb-nav-item--active' : ''}`}>
                            <div className="aptb-nav-item-left">
                                <span className="aptb-nav-item-title">{ch.title}</span>
                                <div className="aptb-nav-progress-track">
                                    <div className="aptb-nav-progress-fill" style={{ width: `${ch.progress}%` }} />
                                </div>
                            </div>
                            {ch.hasQuiz && <span className="aptb-nav-quiz-icon"><HelpCircle size={10} /></span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────

const ApostilasBlock: React.FC = () => {
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            className={`aptb-section ${visible ? 'aptb-section--visible' : ''}`}
            ref={sectionRef}
        >
            <div className="aptb-container">

                {/* ── CABEÇALHO ── */}
                <div className="aptb-header">
                    <p className="aptb-label">Apostilas Inteligentes</p>
                    <h2 className="aptb-headline">
                        Um ambiente de leitura que<br />transforma conteúdo em raciocínio.
                    </h2>
                </div>

                {/* ── GRID ── */}
                <div className="aptb-grid">

                    {/* ─── COLUNA ESQUERDA: DEMO ─── */}
                    <div className="aptb-demo-col">
                        <div className="aptb-mock-shell">
                            <ApostilasDemo active={visible} />
                        </div>
                    </div>

                    {/* ─── COLUNA DIREITA: PILARES ─── */}
                    <div className="aptb-pillars-col">
                        <p className="aptb-pillars-intro">Enquanto você lê, o sistema faz três coisas:</p>
                        {PILLARS.map((pillar) => {
                            const Icon = pillar.icon;
                            return (
                                <div key={pillar.title} className="aptb-pillar">
                                    <div className="aptb-pillar-accent" />
                                    <div className="aptb-pillar-icon">
                                        <Icon size={16} strokeWidth={1.75} />
                                    </div>
                                    <div className="aptb-pillar-body">
                                        <h3 className="aptb-pillar-title">{pillar.title}</h3>
                                        <p className="aptb-pillar-text">{pillar.text}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>

                {/* ── DIFERENCIAL ── */}
                <div className="aptb-diff">
                    <p className="aptb-diff-title">Seu PDF informa. O GRAIA evolui com você.</p>
                    <div className="aptb-diff-list">
                        {[
                            'Não é um arquivo estático. É onde seu raciocínio ganha forma.',
                            'Tudo o que você registra passa a integrar sua base de conhecimento.',
                            'O conteúdo que você lê se transforma automaticamente em revisão ativa.',
                            'O material evolui sem apagar sua trajetória.',
                        ].map(item => (
                            <div key={item} className="aptb-diff-item">
                                <span className="aptb-diff-dot" />
                                <span>{item}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── FECHAMENTO ── */}
                <div className="aptb-closing">
                    <p className="aptb-closing-main">
                        Ler deixa de ser passivo.<br />
                        <strong>Passa a ser construção ativa.</strong>
                    </p>
                    <button className="aptb-cta" onClick={() => navigate('/marketplace')}>
                        Quero estudar de forma ativa
                    </button>
                </div>

            </div>
        </section>
    );
};

export default ApostilasBlock;